import type { CampaignStatus, PlatformProductStatus } from '~/types/graphql';
import type { IUsedTemplateSuffixes } from '../types';

import { usePlatformProductsQuery } from '~/api/graphql/queries/platformProducts.generated';
import { PLATFORM_PRODUCTS_MAX_PAGE_SIZE } from '~/constants/platformProduct';
import { sortByConditions, toRenderedProductTemplateSuffix } from '~/utils';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '~/constants/campaign';
import { toPlatformProductIdsQuery } from '~/modules/campaign-price';
import { useCampaignStore } from '~/modules/campaign/stores';
import { STORE_PLATFORM } from '~/constants/storePlatform';
import { ICampaignType } from '~/modules/campaign/types';
import { useCallback, useMemo } from 'react';

interface IPriceVariantUsage {
  baseID: string;
  priceId: string;
  campaignId: string;
  campaignName?: string;
  campaignStatus?: CampaignStatus;
}

export interface IPriceCampaignTemplateUsageProps {
  excludedCampaignId?: string;
  /**
   * Every template the theme holds a file for, by the bare suffix a product reports. Read here rather
   * than off the assets: the caller shares one pass with the other usage hooks. A product whose
   * suffix has no file left falls back to the default product template, which is what it then holds.
   */
  templateSuffixes: Map<string, string>;
}

export interface IPriceCampaignTemplateUsageResult {
  priceCampaignTemplateUsage: IUsedTemplateSuffixes;
  handleRefetchPriceProducts: () => Promise<unknown>;
  isFetchingPriceProducts: boolean;
}

/**
 * A price test locks the product it runs on, not a template: its variants carry a product
 * (`price.baseID`) and no `templateSuffix`. Which template that product renders only lives on the
 * platform, so the suffix has to be read back from `platformProducts` before the picker can tell
 * that template is already taken.
 */
export const usePriceCampaignTemplateUsage = ({
  excludedCampaignId,
  templateSuffixes,
}: IPriceCampaignTemplateUsageProps): IPriceCampaignTemplateUsageResult => {
  const dataCampaigns = useCampaignStore((state) => state.campaigns);

  const priceVariantUsages = useMemo<IPriceVariantUsage[]>(() => {
    const priceCampaigns = (dataCampaigns ?? []).filter(
      (item) =>
        item.type === CAMPAIGN_TYPE.PRICE &&
        item.status !== CAMPAIGN_STATUS.STOPPED &&
        !item.deletedAt &&
        item.id !== excludedCampaignId,
    );

    return priceCampaigns.flatMap((campaign) =>
      // Control side first: an experiment is collapsed to its first price record, and only removing
      // the Control's record hands the whole pair back — the Variant runs on a product duplicate
      // that would otherwise keep holding the template.
      sortByConditions(campaign.variants ?? [], [{ attr: 'isOrigin' }]).flatMap((variant) => {
        const price = variant?.price;
        if (!price?.baseID) return [];

        return [
          {
            baseID: price.baseID,
            priceId: price.id,
            campaignId: campaign.id,
            campaignName: campaign.name,
            campaignStatus: campaign.status ?? undefined,
          },
        ];
      }),
    );
  }, [dataCampaigns, excludedCampaignId]);

  const baseIDs = useMemo(
    () => [...new Set(priceVariantUsages.map(({ baseID }) => baseID))],
    [priceVariantUsages],
  );

  const { data, refetch, isFetching } = usePlatformProductsQuery(
    {
      parameter: {
        first: Math.min(baseIDs.length, PLATFORM_PRODUCTS_MAX_PAGE_SIZE),
        query: toPlatformProductIdsQuery(baseIDs),
      },
      platform: STORE_PLATFORM.SHOPIFY,
    },
    // Removing a price changes `baseIDs`, and a bare key switch would blank `data` mid-fetch — every
    // price usage would read as gone for a render, which callers watching for "no usages left" act on.
    { enabled: baseIDs.length > 0, keepPreviousData: true },
  );

  const productByBaseId = useMemo(() => {
    const byBaseId: Record<
      string,
      { templateSuffix: string; title?: string; status?: PlatformProductStatus }
    > = {};

    data?.platformProducts.edges?.forEach((edge) => {
      const product = edge?.node;
      if (!product) return;

      byBaseId[product.baseID] = {
        // The platform reports a bare suffix (`sales-page`); usages are keyed by the theme asset key
        // (`templates/product.sales-page.json`) templates are matched on. A suffix the theme has no
        // file for renders the default product template, which is what the test then holds.
        templateSuffix: toRenderedProductTemplateSuffix({
          templateSuffix: product.templateSuffix ?? undefined,
          templateSuffixes,
        }),
        title: product.title ?? undefined,
        status: product.status ?? undefined,
      };
    });

    return byBaseId;
  }, [data, templateSuffixes]);

  const priceCampaignTemplateUsage = useMemo<IUsedTemplateSuffixes>(() => {
    const usage: IUsedTemplateSuffixes = {};

    priceVariantUsages.forEach(({ baseID, priceId, campaignId, campaignName, campaignStatus }) => {
      const product = productByBaseId[baseID];
      if (!product) return;

      (usage[product.templateSuffix] ??= []).push({
        campaignId,
        campaignName,
        campaignStatus,
        campaignType: ICampaignType.PRICE,
        isOtherPriceCampaign: true,
        priceId,
        resourceName: product.title,
        productStatus: product.status,
      });
    });

    return usage;
  }, [priceVariantUsages, productByBaseId]);

  // The suffix a price test holds is only knowable from the platform, and the global staleTime keeps
  // it cached for days — so Refresh has to pull the products again, not just the campaigns.
  const handleRefetchPriceProducts = useCallback(async () => {
    if (!baseIDs.length) return;

    // v4 `refetch()` resolves with an error result instead of rejecting — opt-in throw so
    // withErrorToast at the call site can surface + capture it, matching the other refetches in
    // the same Promise.all.
    return refetch({ throwOnError: true });
  }, [baseIDs.length, refetch]);

  return {
    priceCampaignTemplateUsage,
    handleRefetchPriceProducts,
    isFetchingPriceProducts: isFetching,
  };
};
